import React from "react";
import { Button } from "@mantine/core";

import { PickIcon, BackspaceIcon } from "@piqnt/runtime-core/react/Icon";

import {
  WatchBoolean,
  WatchNumber,
  WatchText,
  WatchVec2,
} from "@piqnt/runtime-core/react/WatchValue";
import {
  Pair,
  PairKey,
  PairValue,
  Subtree,
  SubtreeBody,
  SubtreeHeader,
} from "@piqnt/runtime-core/react/Subtree";

import { useMiddleware } from "@piqnt/runtime-core/react/ReactMount";
import { SpaceContext } from "../space";

import { getLabel } from "@piqnt/runtime-core/common/Label";
import {
  getKey,
  type PlainBody,
  type Vec2Value,
  type PlainDistanceJoint,
  type PlainFrictionJoint,
  type PlainMotorJoint,
  type PlainPrismaticJoint,
  type PlainRevoluteJoint,
  type PlainWeldJoint,
  type PlainWheelJoint,
  type PlainPulleyJoint,
  type PlainRopeJoint,
  type PlainJoint,
} from "@piqnt/runtime-space/drawing-model";

import { LiveBody } from "./LiveBody";
import { LiveJoint } from "./LiveJoint";

interface PropsWithJoint {
  joint: PlainJoint;
  isDraft?: boolean;
}

export const JointView: React.FC<PropsWithJoint> = ({ joint }) => {
  const { context, emit } = useMiddleware<SpaceContext>();
  const editable = context.editable.value;

  if (!joint) return null;
  const type = joint.type;

  return (
    <Subtree open>
      <SubtreeHeader
        onClick={() => emit("select-joint", { join: getKey(joint) })}
      >
        <LiveJoint joint={joint as PlainJoint} />
      </SubtreeHeader>
      <SubtreeBody>
        <Pair>
          <PairKey>type</PairKey>
          <PairValue>{joint.type}</PairValue>
        </Pair>

        <Pair>
          <PairKey>label</PairKey>
          <PairValue>
            <WatchBoolean
              editable={editable}
              getValue={() => joint.collideConnected}
              setValue={(v) =>
                emit("joint-set-props", {
                  joint: getKey(joint),
                  props: { collideConnected: v },
                })
              }
            />
          </PairValue>
        </Pair>

        <Pair>
          <PairKey>label</PairKey>
          <PairValue>
            <WatchText
              editable={editable}
              getValue={() => getLabel(joint)}
              setValue={(v) =>
                emit("joint-set-props", {
                  joint: getKey(joint),
                  props: { lookupId: v },
                })
              }
            />
          </PairValue>
        </Pair>

        {type === "distance-joint" && (
          <EditDistanceJoint editable={editable} joint={joint} />
        )}

        {type === "rope-joint" && (
          <EditRopeJoint editable={editable} joint={joint} />
        )}

        {type === "friction-joint" && (
          <EditFrictionJoint editable={editable} joint={joint} />
        )}

        {type === "prismatic-joint" && (
          <EditPrismaticJoint editable={editable} joint={joint} />
        )}

        {type === "revolute-joint" && (
          <EditRevoluteJoint editable={editable} joint={joint} />
        )}

        {type === "weld-joint" && (
          <EditWeldJoint editable={editable} joint={joint} />
        )}

        {type === "wheel-joint" && (
          <EditWheelJoint editable={editable} joint={joint} />
        )}

        {type === "motor-joint" && (
          <EditMotorJoint editable={editable} joint={joint} />
        )}

        {type === "pulley-joint" && (
          <EditPulleyJoint editable={editable} joint={joint} />
        )}
      </SubtreeBody>
    </Subtree>
  );
};

interface JointBodyProps {
  __key?: string;
  bodyA?: PlainBody;
  localAnchorA?: Vec2Value;
  bodyB?: PlainBody;
  localAnchorB?: Vec2Value;
}

const PickButton: React.FC<{
  name: string;
  value: object | boolean;
  editable: boolean;
  onClear: () => void;
  onPick: () => void;
}> = (props) => {
  if (!props.editable) return null;
  return (
    <Button
      onClick={props.value ? props.onClear : props.onPick}
      title={(props.value ? "Remove " : "Pick ") + props.name}
      variant="transparent"
      size="compact-sm"
      color="gray"
    >
      {props.value ? <BackspaceIcon /> : <PickIcon />}
    </Button>
  );
};

type PropsWithJointBody = {
  joint: JointBodyProps;
  noAnchors?: boolean;
};

export const EditBodies: React.FC<PropsWithJointBody> = ({
  joint,
  noAnchors,
}) => {
  const { context, emit } = useMiddleware<SpaceContext>();
  const editable = context.editable.value;

  return (
    <>
      <Pair>
        <PairKey>
          bodyA
          <PickButton
            editable={editable}
            name="bodyA"
            value={joint.bodyA}
            onPick={() => {
              emit("tool-request", {
                name: "create-joint",
                joint,
                bodyName: "bodyA",
                noAnchor: noAnchors,
              });
            }}
            onClear={() => {
              emit("joint-set-props", {
                joint: joint.__key,
                props: { bodyA: null },
              });
            }}
          />
        </PairKey>
        <PairValue>
          <LiveBody variant="short" body={joint.bodyA} selectable={false} />
        </PairValue>
      </Pair>
      {!noAnchors && (
        <Pair>
          <PairKey>localAnchorA</PairKey>
          <PairValue>
            <WatchVec2
              editable={editable}
              getValue={() => joint.localAnchorA}
              setValue={(v) =>
                emit("joint-set-props", {
                  joint: getKey(joint),
                  props: { localAnchorA: v },
                })
              }
            />
          </PairValue>
        </Pair>
      )}
      <Pair>
        <PairKey>
          bodyB
          <PickButton
            editable={editable}
            name="bodyB"
            value={joint.bodyB}
            onPick={() => {
              emit("tool-request", {
                name: "create-joint",
                joint,
                bodyName: "bodyB",
                noAnchor: noAnchors,
              });
            }}
            onClear={() => {
              emit("joint-set-props", {
                joint: joint.__key,
                props: { bodyB: null },
              });
            }}
          />
        </PairKey>
        <PairValue>
          <LiveBody variant="short" body={joint.bodyB} selectable={false} />
        </PairValue>
      </Pair>
      {!noAnchors && (
        <Pair>
          <PairKey>localAnchorB</PairKey>
          <PairValue>
            <WatchVec2
              editable={editable}
              getValue={() => joint.localAnchorB}
              setValue={(v) =>
                emit("joint-set-props", {
                  joint: getKey(joint),
                  props: { localAnchorB: v },
                })
              }
            />
          </PairValue>
        </Pair>
      )}
    </>
  );
};

const EditDistanceJoint: React.FC<{
  editable: boolean;
  joint: PlainDistanceJoint;
}> = ({ editable, joint }) => {
  const { emit } = useMiddleware<SpaceContext>();

  return (
    <>
      <EditBodies joint={joint} />
      <Pair>
        <PairKey>length</PairKey>
        <PairValue>
          <WatchNumber
            editable={editable}
            allowNull
            getValue={() => joint.length}
            setValue={(v) =>
              emit("joint-set-props", {
                joint: getKey(joint),
                props: {
                  length: v,
                },
              })
            }
          />
        </PairValue>
      </Pair>
      <Pair>
        <PairKey>frequencyHz</PairKey>
        <PairValue>
          <WatchNumber
            editable={editable}
            getValue={() => joint.frequencyHz}
            setValue={(v) =>
              emit("joint-set-props", {
                joint: getKey(joint),
                props: {
                  frequencyHz: v,
                },
              })
            }
          />
        </PairValue>
      </Pair>
      <Pair>
        <PairKey>dampingRatio</PairKey>
        <PairValue>
          <WatchNumber
            editable={editable}
            getValue={() => joint.dampingRatio}
            setValue={(v) =>
              emit("joint-set-props", {
                joint: getKey(joint),
                props: {
                  dampingRatio: v,
                },
              })
            }
          />
        </PairValue>
      </Pair>
    </>
  );
};

const EditRopeJoint: React.FC<{
  editable: boolean;
  joint: PlainRopeJoint;
}> = ({ editable, joint }) => {
  const { emit } = useMiddleware<SpaceContext>();

  return (
    <>
      <EditBodies joint={joint} />
      <Pair>
        <PairKey>maxLength</PairKey>
        <PairValue>
          <WatchNumber
            editable={editable}
            getValue={() => joint.maxLength}
            setValue={(v) =>
              emit("joint-set-props", {
                joint: getKey(joint),
                props: { maxLength: v },
              })
            }
          />
        </PairValue>
      </Pair>
    </>
  );
};

const EditFrictionJoint: React.FC<{
  editable: boolean;
  joint: PlainFrictionJoint;
}> = ({ editable, joint }) => {
  const { emit } = useMiddleware<SpaceContext>();

  return (
    <>
      <EditBodies joint={joint} />
      <Pair>
        <PairKey>maxForce</PairKey>
        <PairValue>
          <WatchNumber
            editable={editable}
            getValue={() => joint.maxForce}
            setValue={(v) =>
              emit("joint-set-props", {
                joint: getKey(joint),
                props: {
                  maxForce: v,
                },
              })
            }
          />
        </PairValue>
      </Pair>
      <Pair>
        <PairKey>maxTorque</PairKey>
        <PairValue>
          <WatchNumber
            editable={editable}
            getValue={() => joint.maxTorque}
            setValue={(v) =>
              emit("joint-set-props", {
                joint: getKey(joint),
                props: {
                  maxTorque: v,
                },
              })
            }
          />
        </PairValue>
      </Pair>
    </>
  );
};

const EditPrismaticJoint: React.FC<{
  editable: boolean;
  joint: PlainPrismaticJoint;
}> = ({ editable, joint }) => {
  const { emit } = useMiddleware<SpaceContext>();

  return (
    <>
      <EditBodies joint={joint} />
      <Pair>
        <PairKey>localAxisA</PairKey>
        <PairValue>
          <WatchVec2
            editable={editable}
            getValue={() => joint.localAxisA}
            setValue={(v) =>
              emit("joint-set-props", {
                joint: getKey(joint),
                props: {
                  localAxisA: v,
                },
              })
            }
          />
        </PairValue>
      </Pair>
      <Pair>
        <PairKey>enableLimit</PairKey>
        <PairValue>
          <WatchBoolean
            editable={editable}
            getValue={() => joint.enableLimit}
            setValue={(v) =>
              emit("joint-set-props", {
                joint: getKey(joint),
                props: {
                  enableLimit: v,
                },
              })
            }
          />
        </PairValue>
      </Pair>
      <Pair>
        <PairKey>lowerTranslation</PairKey>
        <PairValue>
          <WatchNumber
            editable={editable}
            getValue={() => joint.lowerTranslation}
            setValue={(v) =>
              emit("joint-set-props", {
                joint: getKey(joint),
                props: {
                  lowerTranslation: v,
                },
              })
            }
          />
        </PairValue>
      </Pair>
      <Pair>
        <PairKey>upperTranslation</PairKey>
        <PairValue>
          <WatchNumber
            editable={editable}
            getValue={() => joint.upperTranslation}
            setValue={(v) =>
              emit("joint-set-props", {
                joint: getKey(joint),
                props: {
                  upperTranslation: v,
                },
              })
            }
          />
        </PairValue>
      </Pair>
      <Pair>
        <PairKey>enableMotor</PairKey>
        <PairValue>
          <WatchBoolean
            editable={editable}
            getValue={() => joint.enableMotor}
            setValue={(v) =>
              emit("joint-set-props", {
                joint: getKey(joint),
                props: {
                  enableMotor: v,
                },
              })
            }
          />
        </PairValue>
      </Pair>
      <Pair>
        <PairKey>maxMotorForce</PairKey>
        <PairValue>
          <WatchNumber
            editable={editable}
            getValue={() => joint.maxMotorForce}
            setValue={(v) =>
              emit("joint-set-props", {
                joint: getKey(joint),
                props: {
                  maxMotorForce: v,
                },
              })
            }
          />
        </PairValue>
      </Pair>
      <Pair>
        <PairKey>motorSpeed</PairKey>
        <PairValue>
          <WatchNumber
            editable={editable}
            getValue={() => joint.motorSpeed}
            setValue={(v) =>
              emit("joint-set-props", {
                joint: getKey(joint),
                props: {
                  motorSpeed: v,
                },
              })
            }
          />
        </PairValue>
      </Pair>
    </>
  );
};

const EditRevoluteJoint: React.FC<{
  editable: boolean;
  joint: PlainRevoluteJoint;
}> = ({ editable, joint }) => {
  const { emit } = useMiddleware<SpaceContext>();

  return (
    <>
      <EditBodies joint={joint} />
      <Pair>
        <PairKey>enableLimit</PairKey>
        <PairValue>
          <WatchBoolean
            editable={editable}
            getValue={() => joint.enableLimit}
            setValue={(v) =>
              emit("joint-set-props", {
                joint: getKey(joint),
                props: {
                  enableLimit: v,
                },
              })
            }
          />
        </PairValue>
      </Pair>
      <Pair>
        <PairKey>lowerAngle</PairKey>
        <PairValue>
          <WatchNumber
            editable={editable}
            getValue={() => joint.lowerAngle}
            setValue={(v) =>
              emit("joint-set-props", {
                joint: getKey(joint),
                props: {
                  lowerAngle: v,
                },
              })
            }
          />
        </PairValue>
      </Pair>
      <Pair>
        <PairKey>upperAngle</PairKey>
        <PairValue>
          <WatchNumber
            editable={editable}
            getValue={() => joint.upperAngle}
            setValue={(v) =>
              emit("joint-set-props", {
                joint: getKey(joint),
                props: {
                  upperAngle: v,
                },
              })
            }
          />
        </PairValue>
      </Pair>
      <Pair>
        <PairKey>enableMotor</PairKey>
        <PairValue>
          <WatchBoolean
            editable={editable}
            getValue={() => joint.enableMotor}
            setValue={(v) =>
              emit("joint-set-props", {
                joint: getKey(joint),
                props: {
                  enableMotor: v,
                },
              })
            }
          />
        </PairValue>
      </Pair>
      <Pair>
        <PairKey>maxMotorTorque</PairKey>
        <PairValue>
          <WatchNumber
            editable={editable}
            getValue={() => joint.maxMotorTorque}
            setValue={(v) =>
              emit("joint-set-props", {
                joint: getKey(joint),
                props: {
                  maxMotorTorque: v,
                },
              })
            }
          />
        </PairValue>
      </Pair>
      <Pair>
        <PairKey>motorSpeed</PairKey>
        <PairValue>
          <WatchNumber
            editable={editable}
            getValue={() => joint.motorSpeed}
            setValue={(v) =>
              emit("joint-set-props", {
                joint: getKey(joint),
                props: {
                  motorSpeed: v,
                },
              })
            }
          />
        </PairValue>
      </Pair>
    </>
  );
};

const EditWeldJoint: React.FC<{
  editable: boolean;
  joint: PlainWeldJoint;
}> = ({ editable, joint }) => {
  const { emit } = useMiddleware<SpaceContext>();

  return (
    <>
      <EditBodies joint={joint} />
      <Pair>
        <PairKey>referenceAngle</PairKey>
        <PairValue>
          <WatchNumber
            editable={editable}
            getValue={() => joint.referenceAngle}
          />
        </PairValue>
      </Pair>
      <Pair>
        <PairKey>frequencyHz</PairKey>
        <PairValue>
          <WatchNumber
            editable={editable}
            getValue={() => joint.frequencyHz}
            setValue={(v) =>
              emit("joint-set-props", {
                joint: getKey(joint),
                props: {
                  frequencyHz: v,
                },
              })
            }
          />
        </PairValue>
      </Pair>
      <Pair>
        <PairKey>dampingRatio</PairKey>
        <PairValue>
          <WatchNumber
            editable={editable}
            getValue={() => joint.dampingRatio}
            setValue={(v) =>
              emit("joint-set-props", {
                joint: getKey(joint),
                props: {
                  dampingRatio: v,
                },
              })
            }
          />
        </PairValue>
      </Pair>
    </>
  );
};

const EditWheelJoint: React.FC<{
  editable: boolean;
  joint: PlainWheelJoint;
}> = ({ editable, joint }) => {
  const { emit } = useMiddleware<SpaceContext>();

  return (
    <>
      <EditBodies joint={joint} />
      <Pair>
        <PairKey>localAxisA</PairKey>
        <PairValue>
          <WatchVec2
            editable={editable}
            getValue={() => joint.localAxisA}
            setValue={(v) =>
              emit("joint-set-props", {
                joint: getKey(joint),
                props: {
                  localAxisA: v,
                },
              })
            }
          />
        </PairValue>
      </Pair>
      <Pair>
        <PairKey>springFrequencyHz</PairKey>
        <PairValue>
          <WatchNumber
            editable={editable}
            getValue={() => joint.frequencyHz}
            setValue={(v) =>
              emit("joint-set-props", {
                joint: getKey(joint),
                props: {
                  frequencyHz: v,
                },
              })
            }
          />
        </PairValue>
      </Pair>
      <Pair>
        <PairKey>springDampingRatio</PairKey>
        <PairValue>
          <WatchNumber
            editable={editable}
            getValue={() => joint.dampingRatio}
            setValue={(v) =>
              emit("joint-set-props", {
                joint: getKey(joint),
                props: {
                  dampingRatio: v,
                },
              })
            }
          />
        </PairValue>
      </Pair>
      <Pair>
        <PairKey>enableMotor</PairKey>
        <PairValue>
          <WatchBoolean
            editable={editable}
            getValue={() => joint.enableMotor}
            setValue={(v) =>
              emit("joint-set-props", {
                joint: getKey(joint),
                props: {
                  enableMotor: v,
                },
              })
            }
          />
        </PairValue>
      </Pair>
      <Pair>
        <PairKey>maxMotorTorque</PairKey>
        <PairValue>
          <WatchNumber
            editable={editable}
            getValue={() => joint.maxMotorTorque}
            setValue={(v) =>
              emit("joint-set-props", {
                joint: getKey(joint),
                props: {
                  maxMotorTorque: v,
                },
              })
            }
          />
        </PairValue>
      </Pair>
      <Pair>
        <PairKey>motorSpeed</PairKey>
        <PairValue>
          <WatchNumber
            editable={editable}
            getValue={() => joint.motorSpeed}
            setValue={(v) =>
              emit("joint-set-props", {
                joint: getKey(joint),
                props: {
                  motorSpeed: v,
                },
              })
            }
          />
        </PairValue>
      </Pair>
    </>
  );
};

const EditMotorJoint: React.FC<{
  editable: boolean;
  joint: PlainMotorJoint;
}> = ({ editable, joint }) => {
  const { emit } = useMiddleware<SpaceContext>();

  return (
    <>
      <EditBodies joint={joint} noAnchors />
      <Pair>
        <PairKey>linearOffset</PairKey>
        <PairValue>
          <WatchVec2
            editable={editable}
            allowNull
            getValue={() => joint.linearOffset}
            setValue={(v) =>
              emit("joint-set-props", {
                joint: getKey(joint),
                props: { linearOffset: v },
              })
            }
          />
        </PairValue>
      </Pair>
      <Pair>
        <PairKey>angularOffset</PairKey>
        <PairValue>
          <WatchNumber
            editable={editable}
            allowNull
            getValue={() => joint.angularOffset}
            setValue={(v) =>
              emit("joint-set-props", {
                joint: getKey(joint),
                props: { angularOffset: v },
              })
            }
          />
        </PairValue>
      </Pair>
      <Pair>
        <PairKey>maxForce</PairKey>
        <PairValue>
          <WatchNumber
            editable={editable}
            getValue={() => joint.maxForce}
            setValue={(v) =>
              emit("joint-set-props", {
                joint: getKey(joint),
                props: { maxForce: v },
              })
            }
          />
        </PairValue>
      </Pair>
      <Pair>
        <PairKey>maxTorque</PairKey>
        <PairValue>
          <WatchNumber
            editable={editable}
            getValue={() => joint.maxTorque}
            setValue={(v) =>
              emit("joint-set-props", {
                joint: getKey(joint),
                props: { maxTorque: v },
              })
            }
          />
        </PairValue>
      </Pair>
      <Pair>
        <PairKey>correctionFactor</PairKey>
        <PairValue>
          <WatchNumber
            editable={editable}
            getValue={() => joint.correctionFactor}
            setValue={(v) =>
              emit("joint-set-props", {
                joint: getKey(joint),
                props: { correctionFactor: v },
              })
            }
          />
        </PairValue>
      </Pair>
    </>
  );
};

const EditPulleyJoint: React.FC<{
  editable: boolean;
  joint: PlainPulleyJoint;
}> = ({ editable, joint }) => {
  const { emit } = useMiddleware<SpaceContext>();

  return (
    <>
      <EditBodies joint={joint} />
      <Pair>
        <PairKey>groundAnchorA</PairKey>
        <PairValue>
          <WatchVec2
            editable={editable}
            getValue={() => joint.groundAnchorA}
            setValue={(v) =>
              emit("joint-set-props", {
                joint: getKey(joint),
                props: { groundAnchorA: v },
              })
            }
          />
        </PairValue>
      </Pair>
      <Pair>
        <PairKey>groundAnchorB</PairKey>
        <PairValue>
          <WatchVec2
            editable={editable}
            getValue={() => joint.groundAnchorB}
            setValue={(v) =>
              emit("joint-set-props", {
                joint: getKey(joint),
                props: { groundAnchorB: v },
              })
            }
          />
        </PairValue>
      </Pair>
      <Pair>
        <PairKey>lengthA</PairKey>
        <PairValue>
          <WatchNumber
            editable={editable}
            allowNull
            getValue={() => joint.lengthA}
            setValue={(v) =>
              emit("joint-set-props", {
                joint: getKey(joint),
                props: { lengthA: v },
              })
            }
          />
        </PairValue>
      </Pair>
      <Pair>
        <PairKey>lengthB</PairKey>
        <PairValue>
          <WatchNumber
            editable={editable}
            allowNull
            getValue={() => joint.lengthB}
            setValue={(v) =>
              emit("joint-set-props", {
                joint: getKey(joint),
                props: { lengthB: v },
              })
            }
          />
        </PairValue>
      </Pair>
      <Pair>
        <PairKey>ratio</PairKey>
        <PairValue>
          <WatchNumber
            editable={editable}
            getValue={() => joint.ratio}
            setValue={(v) =>
              emit("joint-set-props", {
                joint: getKey(joint),
                props: { ratio: v },
              })
            }
          />
        </PairValue>
      </Pair>
    </>
  );
};
